import { getOperationAST, print, type DocumentNode } from "graphql";
import { GraphQLClient } from "graphql-request";

const API_URL = `${process.env.NEXT_PUBLIC_API_URL}/graphql`;

const customFetch = (input: RequestInfo | URL, init?: RequestInit) => {
  const body = init?.body
    ? (JSON.parse(init.body as string) as Record<string, unknown>)
    : {};
  const operationName = body.operationName || "unnamed";
  const urlWithOperationName = `${input}?op=${operationName}`;
  return fetch(urlWithOperationName, {
    ...init,
    body: JSON.stringify(body),
  });
};

export const graphqlClient = new GraphQLClient(API_URL, {
  credentials: "include",
  fetch: customFetch,
});

/** client with auth for server-side requests (e.g. getServerSideProps) where cookies are not sent */
export function graphqlClientWithAuth(accessToken: string) {
  return new GraphQLClient(API_URL, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Cookie: `${process.env.NEXT_PUBLIC_COOKIE_KEY_COMPANY}=${accessToken}`,
    },
    fetch: customFetch,
  });
}

/**
 * server-only: raw fetch with explicit auth headers so the API receives them.
 * Use when graphql-request client does not forward constructor headers (e.g. with custom fetch).
 */
export async function requestWithAuth<T>(
  accessToken: string,
  document: DocumentNode,
  variables?: Record<string, unknown>,
): Promise<T> {
  const queryString = print(document);
  const operationName = getOperationAST(document)?.name?.value ?? "unnamed";
  const url = `${API_URL}?op=${operationName}`;
  const body = JSON.stringify({
    query: queryString,
    variables: variables ?? {},
    operationName,
  });
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      Cookie: `${process.env.NEXT_PUBLIC_COOKIE_KEY_COMPANY}=${accessToken}`,
    },
    body,
  });
  const json = (await res.json()) as {
    data?: T;
    errors?: Array<{ message: string }>;
  };
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.message ?? "GraphQL error");
  }
  if (json.data == null) {
    throw new Error("No data in GraphQL response");
  }
  return json.data;
}
